import LexicalMessageEditor from '@/common/components/message-editor/lexical/LexicalMessageEditor';
import ReplyToMessageChip from '@/common/components/message-editor/reply-to-message-chip';
import { useAppSelector } from '@/common/reducers/hooks/store.hook';
import AckIcon from '@/components/team-inbox/ack-Icon';
import { makeSelectMessageDraftById } from '@/components/team-inbox/redux/TeamInboxSelectors';
import { getSenderColor } from '@/components/team-inbox/team-inbox-helpers';
import { Box, Typography } from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { formatInLanguage } from '@common/config/moment-locales';
import AddScheduledMessageLinkPreview from './add-scheduled-message-link-preview';
import WhatsAppPollPreview from '@common/components/message-editor/whatsapp-poll-preview';
import AddScheduledMessageAttachmentPreview from './add-schedulued-message-attachment-preview';
import MediaLoadingIndicator from './media-loading-indicator';
import { UserMessage } from '@bt-common/schemas/bt/user-message';

// Define the design variables type
interface Design {
  waMessageBg?: string;
  bubbleBorderRadius?: string;
}

// Define the props for the component
interface PreviewMessageBubbleProps {
  messageId?: string;
  /** Render this message directly instead of resolving one from the redux draft
   *  store — lets the bubble preview an arbitrary message (e.g. a campaign
   *  template in the campaigns list) without seeding the editor draft. Threaded
   *  to the link/attachment/poll child previews too. */
  messageOverride?: Partial<UserMessage>;
  design?: Design;
  senderName?: string;
  shouldDisplayAckIcon?: boolean;
  mediaLoadingState?: 'notFetched' | 'loading' | 'loaded' | 'error';
  isMediaClickable?: boolean;
  /** Cap on the bubble's width inside its container. The 85% default mimics
   *  WhatsApp's chat column; tight gallery cards pass '100%' so the bubble can
   *  use the full panel. */
  maxWidth?: string | number;
  /** Stretch the bubble to fill its container (gallery cards) instead of the
   *  chat-like fit-content sizing. */
  fullWidth?: boolean;
}

// Default design variables
const defaultDesignVars: Design = {
  waMessageBg: 'var(--whatsapp-bubble-sent)',
  bubbleBorderRadius: '12px 0 12px 12px'
};

export default function PreviewMessageBubble({ messageId, messageOverride, design, senderName, shouldDisplayAckIcon = true, mediaLoadingState = 'notFetched', isMediaClickable = false, maxWidth = '85%', fullWidth = false }: PreviewMessageBubbleProps) {
  // Subscribes this bubble to language changes AND supplies the language the
  // timestamp is formatted in (see formatInLanguage — moment's global locale
  // lands after react-i18next has already re-rendered).
  const { i18n } = useTranslation();
  // Use messageDraft for real-time preview updates (avoids race conditions with wrapper)
  const reduxDraft = useAppSelector(state => makeSelectMessageDraftById(messageId ?? null)(state));
  const messageDraft = messageOverride ?? reduxDraft;

  // Compute the actual design variables, allowing override via props.
  // fullWidth (flush gallery mode) squares the bubble — rounded corners would
  // leave notches against the container the bubble is meant to fill.
  const designVars: Design = {
    ...defaultDesignVars,
    ...(fullWidth ? { bubbleBorderRadius: '0px' } : {}),
    ...design
  };

  // Message tail triangle style based on tailPosition
  let triangleStyle;
  
  // LOGICAL properties, not left/right: WhatsApp mirrors for Hebrew/Arabic users
  // (your own messages sit on the left there), and physical `right: -7px` left
  // the tail stranded on the far side of the bubble it belongs to.
  // inline-start/end resolve per direction, so the tail follows the bubble.
  if (messageDraft?.waMessageKey?.fromMe === false) {
    // Received: tail on the inline START edge.
    triangleStyle = {
      position: 'absolute',
      top: '0px',
      insetInlineStart: '-7px',
      width: 0,
      height: 0,
      borderTop: '8px solid var(--whatsapp-bubble-received)',
      borderInlineEnd: '0px solid transparent',
      borderInlineStart: '8px solid transparent',
      borderBottom: '0px solid transparent'
    };
  } else {
    // Sent (default when fromMe is missing): tail on the inline END edge.
    triangleStyle = {
      position: 'absolute',
      top: '0px',
      insetInlineEnd: '-7px',
      width: 0,
      height: 0,
      borderTop: '8px solid var(--whatsapp-bubble-sent)',
      borderInlineEnd: '8px solid transparent',
      borderInlineStart: '0px solid transparent',
      borderBottom: '0px solid transparent'
    };
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      maxWidth: fullWidth ? '100%' : maxWidth,
      minWidth: fullWidth ? 0 : '200px',
      width: fullWidth ? '100%' : 'fit-content'
    }}>
      {/* Message bubble with tail */}
      <Box sx={{
        position: 'relative',
        width: '100%'
      }}>
        <Box sx={{
          bgcolor: designVars.waMessageBg,
          // Pair the bubble's INK with its own background. Without this the text
          // inherits whatever ink the surrounding surface uses, which is fine on a
          // light bubble and wrong on a dark one: inside `.campaigns-rd` in dark
          // mode the sequence card's muted `--seq-ink-2` (#A2A5BC) landed on the
          // dark bubble (#005c4b) at 3.28:1 — measured in the extension, where
          // WhatsApp supplies the `.dark` class. `--whatsapp-text` is defined in
          // BOTH schemes alongside `--whatsapp-bubble-sent`, so the two always move
          // together: 11.2:1 light (#333 on #d9fdd3), 7.2:1 dark (#f1f5f9 on #005c4b).
          color: 'var(--whatsapp-text)',
          borderRadius: designVars.bubbleBorderRadius,
          p: 1.5,
          width: '100%',
          boxSizing: 'border-box',
          boxShadow: '0 1px 0.5px var(--modal-shadow)',
          minWidth: 0
        }}>
          {/* Sender Name */}
          {senderName && (
            <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: getSenderColor(senderName),
                  fontSize: '0.95rem',
                  letterSpacing: 0.01,
                  lineHeight: 1.2,
                  opacity: 0.95,
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%'
                }}
              >
                {senderName}
              </Typography>
            </Box>
          )}
          <Box dir="auto" >
            <ReplyToMessageChip height='83px' />

            {/* A poll carries no media and no link preview — WhatsApp has no
                such thing, and they are not sent — so a leftover attachment or
                link from the message draft is not shown on a poll. */}
            {!messageDraft?.isPoll && (
              <>
                {/* Show link preview first */}
                <AddScheduledMessageLinkPreview messageId={messageId} messageOverride={messageOverride} />

                {/* Show media loading indicator */}
                <MediaLoadingIndicator mediaLoadingState={mediaLoadingState} />

                {/* Show attachment if exists */}
                <AddScheduledMessageAttachmentPreview messageId={messageId} messageOverride={messageOverride} isMediaClickable={isMediaClickable} />
              </>
            )}
            {messageDraft?.message && !messageDraft?.isPoll && (
              <>
                {/* Show formatted message text */}
                <Box
                  sx={{
                    fontSize: '0.9375rem',
                    lineHeight: 1.4,
                    mt: messageDraft.linkPreview ? 1 : 0,
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    '& .lexical-editor': {
                      padding: '0px !important',
                      minHeight: 'auto !important',
                      // A preview bubble must never scroll inside itself — kill
                      // the editor's editing-mode height cap; whatever renders
                      // the bubble decides how to clip it.
                      maxHeight: 'none !important',
                      overflow: 'visible !important'
                    }
                  }}
                  dir='auto'
                >
                  <LexicalMessageEditor 
                    isPreview={true}
                    content={messageDraft?.message}
                    onFilesSelected={() => {}}
                  />
                </Box>
              </>
            )}

            {/* Show poll if exists */}
            {messageDraft?.isPoll && <WhatsAppPollPreview poll={messageDraft.poll} />}
          </Box>

          {/* Time and status */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            mt: 0.5,
            color: 'var(--whatsapp-text)',
            fontSize: '0.75rem',
            flexShrink: 0
          }}>
            {/* Isolated so bidi can't reorder it. Inside an RTL bubble a bare
                "10:46 AM" renders as "AM 10:46" — the Latin meridiem is a weak
                run and gets pulled to the RTL side. `dir="auto"` picks the
                direction from the string's own first strong character, so an
                English "10:46 AM" stays LTR while a Hebrew
                "10:46 לפנות בוקר" stays RTL; the isolate keeps either from
                leaking into the ack icon beside it. */}
            {messageDraft?.dueDate && (messageDraft as { dueDateSetByUser?: boolean }).dueDateSetByUser && (
              <Box component="span" dir="auto" sx={{ unicodeBidi: 'isolate' }}>
                {formatInLanguage(messageDraft.dueDate, i18n.language, 'h:mm A')}
              </Box>
            )}
            {shouldDisplayAckIcon && <AckIcon ackValue={messageDraft?.ack ?? 0} />}
          </Box>
          {/* Debug: Show message key when bt-debug is enabled */}
          {typeof localStorage !== 'undefined' && localStorage.getItem('bt-debug') === 'true' && messageId && (
            <Box sx={{
              fontSize: '0.65rem',
              color: '#888',
              mt: 0.25,
              wordBreak: 'break-all',
              textAlign: 'right',
              fontFamily: 'monospace',
              opacity: 0.7
            }}>
              {messageId}
            </Box>
          )}
        </Box>

        {/* Message tail triangle at top left or right */}
        <Box sx={triangleStyle} />
      </Box>
    </Box>
  )
}